import { CircularProgress, Modal } from '@material-ui/core';
import React, { useEffect, useState, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Button from '../../../components/common/Button';
import ModalContent from '../../../components/common/Modal/ModalContent';
import CoursesAPI from '../../CoursesContainer/course-api';
import Alert from '../../../components/common/Alert';

const CoursesModal = ({ open, onClose, announcement, handleOpen }) => {
  // Estado para mostrar el spinner al guardar el orden
  const [orderSaving, setOrderSaving] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [message, setMessage] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorCourses, setEditorCourses] = useState([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  // Modal de reordenar cursos
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderedCourses, setOrderedCourses] = useState([]);
  // Estado para el ancho dinámico del modal de reordenar
  const [orderModalWidth, setOrderModalWidth] = useState(480);

  // Función para medir el ancho del nombre más largo
  const getTextWidth = (text, font = '500 15px Arial') => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    return context.measureText(text).width;
  };

  // Abrir modal de reordenar cursos
  const openOrderModal = () => {
    const selectedCourses = courses.filter(c => c.checked);
    setOrderedCourses(selectedCourses);
    setOrderSearch('');
    setIsOrderModalOpen(true);
    // Calcular el nombre más largo y ajustar el ancho
    if (selectedCourses.length > 0) {
      const maxLengthName = selectedCourses.reduce((max, c) => c.name.length > max.length ? c.name : max, '');
      const textWidth = getTextWidth(maxLengthName);
      // Sumar espacio para íconos y padding (~180px extra)
      const modalWidth = Math.min(Math.max(480, textWidth + 180), window.innerWidth * 0.95);
      setOrderModalWidth(modalWidth);
    } else {
      setOrderModalWidth(480);
    }
  };
  
  // Utilidad: construye el arreglo de cursos con "checked" y "order" y lo ordena
  const buildCheckedCourses = (allCourses, announcementCourses) => {
    const orderByCourseId = new Map(
      Array.isArray(announcementCourses)
        ? announcementCourses.map((ac) => [ac && ac.courseId && ac.courseId.id, ac && ac.order])
        : []
    );

    // using active courses only
    const checked = (allCourses?.cursos.filter(c => c.active) || []).map((c) => {
      const order = orderByCourseId.get(c.id);
      const isSelected = orderByCourseId.has(c.id); // Si existe en el map, está seleccionado
      return isSelected
        ? { ...c, checked: true, order: order !== null ? order : null }
        : { ...c, checked: false, order: null };
    });
    
    // Separar cursos con orden y sin orden
    const coursesWithOrder = checked.filter(c => c.checked && c.order !== null && c.order !== undefined);
    const coursesWithoutOrder = checked.filter(c => c.checked && (c.order === null || c.order === undefined));
    const coursesUnchecked = checked.filter(c => !c.checked);

    // Ordenar cursos con orden por su valor de orden
    coursesWithOrder.sort((a, b) => a.order - b.order);
    
    // Ordenar cursos sin orden por nombre ASC
    coursesWithoutOrder.sort((a, b) => a.name.localeCompare(b.name));
    
    // Ordenar cursos no seleccionados por nombre ASC
    coursesUnchecked.sort((a, b) => a.name.localeCompare(b.name));

    // Combinar: primero los que tienen orden, luego los que no tienen orden (ordenados por nombre), luego los no seleccionados
    const sortedCourses = [...coursesWithOrder, ...coursesWithoutOrder, ...coursesUnchecked];


    return sortedCourses;
  };

  // Función reutilizable: obtiene y prepara la lista de cursos asociados al announcement
  const fetchAnnouncementCourses = async () => {
    if (!announcement) return [];
    try {
      setLoading(true);
      const [{ data: allCourses }, { data: announcementCourses }] = await Promise.all([
        CoursesAPI.getCourses(1, 1000),
        CoursesAPI.getAllAnnouncementsByCourseId(announcement),
      ]);

      const prepared = buildCheckedCourses(allCourses, announcementCourses);
      setCourses(prepared);
      return prepared;
    } catch (error) {
      setMessage({ type: 'error', message: 'Error al cargar los cursos.' });
      return [];
    } finally {
      setLoading(false);
    }
  };
  // Buscador para cursos seleccionados en el primer modal
  const [selectedSearch, setSelectedSearch] = useState('');
  // Toggle seleccionar todos en la columna izquierda (disponibles)
  const handleSelectAllLeft = () => {
    const disponibles = editorCourses.filter(c => !c.selected);
    const todosSeleccionados = disponibles.every(c => c.tempChecked);
    setEditorCourses(editorCourses.map(c =>
      !c.selected ? { ...c, tempChecked: !todosSeleccionados } : c
    ));
  };

  // Toggle seleccionar todos en la columna derecha (seleccionados)
  const handleSelectAllRight = () => {
    const seleccionados = editorCourses.filter(c => c.selected);
    const todosDesSeleccionados = seleccionados.every(c => !c.tempChecked);
    setEditorCourses(editorCourses.map(c =>
      c.selected ? { ...c, tempChecked: todosDesSeleccionados } : c
    ));
  };

  // Drag and drop handler
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(orderedCourses);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setOrderedCourses(items);
  };

  useEffect(() => {
    if (open && announcement) {
      fetchAnnouncementCourses();
    }
  }, [open, announcement]);

  // Abrir editor y preparar datos
  const openEditor = () => {
    setEditorCourses(
      courses.map((c) => ({
        id: c.id,
        name: c.name,
        selected: c.checked,
        tempChecked: false // por defecto des-seleccionados en ambas columnas
      }))
    );
    setLeftSearch('');
    setRightSearch('');
    setIsEditorOpen(true);
  };

  
  // Guardar el nuevo orden de cursos en el modal de reordenar
  const handleOrderSave = async () => {
    if (!announcement || orderedCourses.length === 0) return;
    setOrderSaving(true);
    const payload = {
      announcement,
      courses: orderedCourses.map((course, idx) => ({
        courseId: course.id,
        order: idx + 1
      }))
    };
    try {
      await CoursesAPI.updateOrderCourseAnnouncement(announcement, payload);
      await fetchAnnouncementCourses();
      setMessage('Orden guardado exitosamente.');
      setIsOrderModalOpen(false);
    } catch (error) {
      setMessage('Hubo un error al guardar el orden de los cursos.');
    } finally {
      setOrderSaving(false);
    }
  };

  // Guardar cambios desde el editor
  const handleEditorSave = async (selectedCourses) => {
    // Calcular el siguiente valor de order disponible
    const existingOrders = selectedCourses
      .map(c => typeof c.order === 'number' ? c.order : null)
      .filter(order => order !== null && !isNaN(order));
    let nextOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1;

    // Asignar order a los cursos nuevos (order null)
    const selectedWithOrder = selectedCourses.map(c => {
      if (typeof c.order === 'number' && !isNaN(c.order)) {
        return { id: c.id, order: c.order };
      } else {
        const assignedOrder = nextOrder;
        nextOrder++;
        return { id: c.id, order: assignedOrder };
      }
    });
    try {
      await CoursesAPI.associateCoursesWithAnnouncement(announcement, selectedWithOrder);
      setMessage('Cursos guardados exitosamente.');
      // Actualizar cursos en el modal principal
      setCourses((prevCourses) =>
        prevCourses.map((c) => ({ ...c, checked: selectedWithOrder.some(sel => sel.id === c.id) }))
      );
      setSelectedCourseIds(selectedWithOrder.map(c => c.id));
    } catch (error) {
      setMessage('Hubo un error al guardar los cursos.');
    }
    setIsEditorOpen(false);
    setSelectedSearch(''); // Limpiar filtro al guardar
  };

  // Cancelar edición
  const handleEditorCancel = () => {
    setIsEditorOpen(false);
    setSelectedSearch(''); // Limpiar filtro al cancelar
  };

  const associateCourse = async () => {
    if (selectedCourseIds.length === 0) {
      return;
    }

    try {
      await CoursesAPI.associateCoursesWithAnnouncement(
        announcement,
        selectedCourseIds
      );

      setMessage('Cursos guardados exitosamente.');
    } catch (error) {
      setMessage('Hubo un error al guardar los cursos.');
    }
  };

  // Limpiar filtro y mensaje al cerrar el modal principal
  useEffect(() => {
    if (!open) {
      setSelectedSearch('');
      setMessage(null);
    }
  }, [open]);

  return (
    <>
      <Modal open={open} onClose={onClose} className="modal">
        <ModalContent title="Gestión de cursos" onClick={onClose}>
          {message && (
            <div className="message-container">
              <Alert type={message.includes('error') ? 'error' : 'success'}>
                {message}
              </Alert>
            </div>
          )}
          <div className="box">
            <div className="box__header">
              <div
                style={{
                  background: '#f2f7fe',
                  color: '#1565c0',
                  borderRadius: '8px',
                  padding: '12px 12px',
                  fontSize: '16px',
                  display: 'inline-block',
                }}
              >
                <b style={{ fontWeight: 700, fontSize: '18px' }}>
                  {courses.filter((c) => c.checked).length}
                </b>{' '}
                cursos seleccionados de {courses.length} disponibles
              </div>
            </div>
            <hr />
            {/* Lista de cursos seleccionados con buscador */}
            {courses.filter((c) => c.checked).length > 0 && (
              <div>
                <span
                  style={{
                    fontWeight: 500,
                    marginBottom: '6px',
                    display: 'block',
                  }}
                >
                  Cursos seleccionados:
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input
                    type="text"
                    placeholder="Buscar curso..."
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: 14, background: '#fff', marginBottom: 0 }}
                    value={selectedSearch}
                    onChange={e => setSelectedSearch(e.target.value)}
                  />
                </div>
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    padding: '10px 16px',
                    maxHeight: '180px',
                    overflowY: 'auto',
                  }}
                >
                  {courses
                    .filter((c) => c.checked && (!selectedSearch || c.name.toLowerCase().includes(selectedSearch.toLowerCase())))
                    .map((course) => (
                      <div
                        key={course.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#388e3c',
                          marginBottom: '4px',
                          fontSize: '15px',
                        }}
                      >
                        {/* Ícono tipo ticket/check */}
                        <svg
                          style={{ marginRight: '8px' }}
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"
                            fill="#388e3c"
                          />
                        </svg>
                        {course.name}
                      </div>
                    ))}
                  {courses.filter((c) => c.checked && (!selectedSearch || c.name.toLowerCase().includes(selectedSearch.toLowerCase()))).length === 0 && (
                    <div style={{ textAlign: 'center', color: '#888', padding: '32px 0' }}>
                      {selectedSearch ? 'No se encontraron cursos' : 'No hay cursos seleccionados'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal__action-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button secondary onClick={openOrderModal}>
              Reordenar Cursos
            </Button>
            <Button onClick={openEditor}>
              Asignar Cursos
            </Button>
          </div>
      {/* Modal de reordenar cursos con drag and drop */}
      {isOrderModalOpen && (
        <Modal open={isOrderModalOpen} onClose={() => setIsOrderModalOpen(false)} className="modal">
          <div
            style={{
              width: orderModalWidth,
              minWidth: orderModalWidth,
              maxWidth: '95vw',
              maxHeight: '90vh',
              margin: '40px auto',
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
              padding: '32px',
              overflow: 'hidden',
              transition: 'width 0.2s',
              position: 'relative',
            }}
          >
            {orderSaving && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}>
                {/* Spinner centrado */}
                <div style={{ width: 60, height: 60 }}>
                  {/* Usar el Spinner común del proyecto */}
                  {require('../../../components/common/Spinner').default({})}
                </div>
              </div>
            )}
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>Reordenar cursos</h2>
            <div style={{ color: '#222', fontSize: '15px', marginBottom: 12 }}>
              Arrastra los cursos para cambiar su orden de presentación
            </div>
            <div style={{ background: '#f7f8fa', borderRadius: 8, border: '1px solid #e0e0e0', padding: '12px 0', maxHeight: '340px', overflowY: 'auto', marginBottom: 16 }}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="coursesList">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {orderedCourses.length > 0 ? (
                        orderedCourses.map((course, idx) => (
                          <Draggable key={course.id} draggableId={course.id.toString()} index={idx}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  background: '#fff',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 8,
                                  margin: '8px 16px',
                                  padding: '10px 16px',
                                  boxShadow: snapshot.isDragging ? '0 2px 8px rgba(21,101,192,0.10)' : 'none',
                                  ...provided.draggableProps.style
                                }}
                              >
                                {/* Icono drag */}
                                <svg width="20" height="20" style={{ marginRight: 12, color: '#bdbdbd', flexShrink: 0 }} fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="5" y="6" width="2" height="2" rx="1" fill="#bdbdbd"/><rect x="5" y="10" width="2" height="2" rx="1" fill="#bdbdbd"/><rect x="5" y="14" width="2" height="2" rx="1" fill="#bdbdbd"/><rect x="13" y="6" width="2" height="2" rx="1" fill="#bdbdbd"/><rect x="13" y="10" width="2" height="2" rx="1" fill="#bdbdbd"/><rect x="13" y="14" width="2" height="2" rx="1" fill="#bdbdbd"/></svg>
                                {/* Check icon */}
                                <svg width="18" height="18" style={{ marginRight: 8, color: '#388e3c', flexShrink: 0 }} fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" fill="#388e3c"/></svg>
                                <span style={{ fontWeight: 500, fontSize: 15, color: '#222', flex: 1 }}>{course.name}</span>
                                <span style={{ fontWeight: 600, fontSize: 13, color: '#888', background: '#f2f7fe', borderRadius: 6, padding: '2px 10px', marginLeft: 12 }}>#{idx + 1}</span>
                              </div>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <div style={{ textAlign: 'center', color: '#888', padding: '32px 0' }}>
                          No hay cursos seleccionados
                        </div>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <div style={{ background: '#fffde7', borderRadius: 8, padding: '12px 16px', fontSize: 15, color: '#b28704', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <svg width="20" height="20" fill="none" style={{ marginRight: 6 }} xmlns="http://www.w3.org/2000/svg"><path d="M10 2a8 8 0 100 16 8 8 0 000-16zm.75 12.25h-1.5v-1.5h1.5v1.5zm0-3h-1.5V5.75h1.5v3.5z" fill="#b28704"/></svg>
              <span><b>Tip:</b> Arrastra los cursos desde el ícono de líneas para cambiar su orden</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
              <Button secondary onClick={() => setIsOrderModalOpen(false)}>
                Volver
              </Button>
              <Button onClick={handleOrderSave} disabled={orderSaving}>
                Guardar
              </Button>
            </div>
          </div>
        </Modal>
      )}
        </ModalContent>
      </Modal>

      {/* Modal Editor de Cursos tipo transfer */}
      {isEditorOpen && (
        <Modal open={isEditorOpen} onClose={handleEditorCancel} className="modal">
          <div style={{ width: '900px', maxWidth: '95vw', maxHeight: '90vh', margin: '40px auto', background: '#fff', borderRadius: '12px', boxShadow: '0 2px 16px rgba(0,0,0,0.12)', padding: '32px', overflow: 'hidden' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 16 }}>Editar cursos</h2>
            <div style={{ color: '#6b7280', fontSize: '15px', marginBottom: 18 }}>
              Selecciona los cursos correspondientes a la convocatoria
            </div>
            <div style={{ display: 'flex', gap: 24, height: 380 }}>
              {/* Columna izquierda - Cursos NO seleccionados */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff' }}>
                <div style={{ padding: '16px 16px 8px 16px', borderBottom: '1px solid #e0e0e0', background: '#f7f8fa', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>Cursos disponibles para seleccionar</span>
                    <span style={{ fontWeight: 600, fontSize: 15, color: '#1565c0', background: '#e3f2fd', borderRadius: 6, padding: '2px 10px' }}>{editorCourses.filter(c => !c.selected).length}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <button style={{ fontSize: 13, padding: '4px 10px', border: '1px solid #bdbdbd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
                      onClick={handleSelectAllLeft}
                      disabled={editorCourses.filter(c => !c.selected).length === 0}
                    >Seleccionar todos</button>
                    <input
                      type="text"
                      placeholder="Buscar cursos..."
                      style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: 14, background: '#fff', marginBottom: 0 }}
                      value={leftSearch}
                      onChange={e => setLeftSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                  {editorCourses
                    .filter(c => !c.selected && (!leftSearch || c.name.toLowerCase().includes(leftSearch.toLowerCase())))
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(course => (
                      <div key={course.id} style={{ display: 'flex', alignItems: 'center', padding: '6px 0' }}>
                        <input type="checkbox" checked={!!course.tempChecked} onChange={e => setEditorCourses(editorCourses.map(c => c.id === course.id ? { ...c, tempChecked: e.target.checked } : c))} />
                        <span style={{ marginLeft: 8, fontSize: 14 }}>{course.name}</span>
                      </div>
                    ))}
                  {editorCourses.filter(c => !c.selected && (!leftSearch || c.name.toLowerCase().includes(leftSearch.toLowerCase()))).length === 0 && (
                    <div style={{ textAlign: 'center', color: '#888', padding: '32px 0' }}>
                      {leftSearch ? 'No se encontraron cursos' : 'Todos los cursos están seleccionados'}
                    </div>
                  )}
                </div>
              </div>
              {/* Botones de transferencia */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12 }}>
                <button style={{ padding: 8, borderRadius: 6, border: '1px solid #bdbdbd', background: '#fff', cursor: 'pointer' }}
                  onClick={() => setEditorCourses(editorCourses.map(c => {
                    if (c.tempChecked && !c.selected) {
                      // Mover de izquierda a derecha y des-seleccionar
                      return { ...c, selected: true, tempChecked: false };
                    }
                    return c;
                  }))}
                  disabled={editorCourses.filter(c => c.tempChecked && !c.selected).length === 0}
                >→</button>
                <button style={{ padding: 8, borderRadius: 6, border: '1px solid #bdbdbd', background: '#fff', cursor: 'pointer' }}
                  onClick={() => setEditorCourses(editorCourses.map(c => {
                    if (c.tempChecked && c.selected) {
                      // Mover de derecha a izquierda y des-seleccionar
                      return { ...c, selected: false, tempChecked: false };
                    }
                    return c;
                  }))}
                  disabled={editorCourses.filter(c => c.tempChecked && c.selected).length === 0}
                >←</button>
              </div>
              {/* Columna derecha - Cursos seleccionados */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff' }}>
                <div style={{ padding: '16px 16px 8px 16px', borderBottom: '1px solid #e0e0e0', background: '#f7f8fa', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>Cursos seleccionados</span>
                    <span style={{ fontWeight: 600, fontSize: 15, color: '#388e3c', background: '#e8f5e9', borderRadius: 6, padding: '2px 10px' }}>{editorCourses.filter(c => c.selected).length}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <button style={{ fontSize: 13, padding: '4px 10px', border: '1px solid #bdbdbd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
                      onClick={handleSelectAllRight}
                      disabled={editorCourses.filter(c => c.selected).length === 0}
                    >Seleccionar todos</button>
                    <input
                      type="text"
                      placeholder="Buscar cursos..."
                      style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: 14, background: '#fff', marginBottom: 0 }}
                      value={rightSearch}
                      onChange={e => setRightSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                  {editorCourses.filter(c => c.selected && (!rightSearch || c.name.toLowerCase().includes(rightSearch.toLowerCase()))).map(course => (
                    <div key={course.id} style={{ display: 'flex', alignItems: 'center', padding: '6px 0' }}>
                      <input type="checkbox" checked={!!course.tempChecked} onChange={e => setEditorCourses(editorCourses.map(c => c.id === course.id ? { ...c, tempChecked: e.target.checked } : c))} />
                      <span style={{ marginLeft: 8, fontSize: 14 }}>{course.name}</span>
                    </div>
                  ))}
                  {editorCourses.filter(c => c.selected && (!rightSearch || c.name.toLowerCase().includes(rightSearch.toLowerCase()))).length === 0 && (
                    <div style={{ textAlign: 'center', color: '#888', padding: '32px 0' }}>
                      {rightSearch ? 'No se encontraron cursos' : 'No hay cursos seleccionados'}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, paddingTop: 24, borderTop: '1px solid #e0e0e0', marginTop: 24 }}>
              <Button secondary onClick={handleEditorCancel}>
                Volver
              </Button>
              <Button onClick={() => handleEditorSave(editorCourses.filter(c => c.selected))}>
                Guardar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CoursesModal;
